import { useEffect, useState } from 'react';

import { centsToCurrency, currencyToCents } from '@/util/number';
import { CalculateCost } from '@/util/payments';

import { JobDraftV2 } from '../../context';
import { createOrderV2Client } from '../../api/client';
import { BillingAccountV2, SkillListItem } from '../../api/types';

import {
  CardTopRow,
  JobList,
  MutedNote,
  PageShell,
  RateCard,
  RateInput,
  RateInputDollar,
  RateInputShell,
  RateLabel,
  RateRow,
  RatingDot,
  RatingRow,
  SkillTitle,
  StepHeading,
  StepSubheading,
  TotalHoursLabel,
} from './styles';

type Tone = 'bad' | 'avg' | 'good' | 'neutral';

type RateRange = { minimum: number; maximum: number } | null;

type PayRateStepProps = {
  jobs: JobDraftV2[];
  onUpdateJobs: (jobs: JobDraftV2[]) => void;
  skills?: SkillListItem[];
  billingAccount: BillingAccountV2 | null;
  onValidityChange?: (valid: boolean) => void;
};

const totalShiftHours = (job: JobDraftV2) =>
  (job.shifts || []).reduce((sum, shift) => {
    const start = new Date(shift.start_at).getTime();
    const end = new Date(shift.end_at).getTime();
    let diffMs = end - start;
    if (diffMs <= 0) diffMs += 24 * 3_600_000;
    const hours = diffMs / 3_600_000 - (shift.break_minutes || 0) / 60;
    return sum + Math.max(hours, 0);
  }, 0);

const toneFromValue = (cents: number, range: RateRange): Tone => {
  if (!range || range.maximum <= 0) return 'neutral';
  if (cents <= 0) return 'neutral';
  if (cents < range.minimum) return 'bad';
  if (cents < range.maximum) return 'avg';
  return 'good';
};

const messageForTone = (tone: Tone) => {
  switch (tone) {
    case 'bad':
      return 'Raise the rate to meet minimum wage.';
    case 'avg':
      return 'Consider an increase to attract candidates.';
    case 'good':
      return 'Great — this is a competitive rate!';
    default:
      return 'Enter a pay rate to see how it compares to the local market.';
  }
};

type JobRateCardProps = {
  job: JobDraftV2;
  index: number;
  skills?: SkillListItem[];
  billingAccountId: number;
  markupPercent: number;
  onUpdateJob: (next: JobDraftV2) => void;
  onRangeResolved: (index: number, range: RateRange) => void;
};

const JobRateCard = ({
  job,
  index,
  skills,
  billingAccountId,
  markupPercent,
  onUpdateJob,
  onRangeResolved,
}: JobRateCardProps) => {
  const skill = skills?.find((s) => s.id === job.skill_id) || null;
  const skillName = skill?.display_name_en || skill?.name || `Job ${index + 1}`;
  const hours = totalShiftHours(job) * (job.quantity || 1);

  const [range, setRange] = useState<RateRange>(null);
  // The input is the source of truth while the user is typing. We only seed
  // it once from job.pay_rate so re-commits from onChange don't snap a
  // half-typed value like "21" up to "21.00".
  const [payInput, setPayInput] = useState<string>(() =>
    job.pay_rate ? centsToCurrency(job.pay_rate) : '',
  );

  useEffect(() => {
    if (!job.skill_id || !job.address_id) {
      setRange(null);
      onRangeResolved(index, null);
      return;
    }
    let cancelled = false;
    createOrderV2Client
      .getRateRange(billingAccountId, job.skill_id, job.address_id)
      .then((next) => {
        if (cancelled) return;
        setRange(next || null);
        onRangeResolved(index, next || null);
      })
      .catch(() => {
        if (cancelled) return;
        setRange(null);
        onRangeResolved(index, null);
      });
    return () => {
      cancelled = true;
    };
  }, [billingAccountId, job.skill_id, job.address_id]);

  const payValueCents = Math.round(
    (Number(payInput.replace(/[$,]/g, '')) || 0) * 100,
  );
  const tone = toneFromValue(payValueCents, range);

  const commitPay = (raw: string) => {
    const trimmed = raw.trim();
    const cents = trimmed ? Math.round(currencyToCents(trimmed)) : 0;
    if (Number.isNaN(cents)) return;
    const cost = CalculateCost(trimmed || '0', markupPercent ?? 0);
    onUpdateJob({ ...job, pay_rate: cents, cost_rate: cost });
  };

  // Strip everything but digits and a single decimal point so users can't
  // type letters or other separators. Anything that parses to a finite
  // number is committed live so the wizard's Continue button enables
  // without waiting for blur.
  const handlePayInputChange = (raw: string) => {
    const digitsAndDot = raw.replace(/[^0-9.]/g, '');
    const firstDot = digitsAndDot.indexOf('.');
    const sanitized =
      firstDot === -1
        ? digitsAndDot
        : digitsAndDot.slice(0, firstDot + 1) +
          digitsAndDot.slice(firstDot + 1).replace(/\./g, '');
    setPayInput(sanitized);
    if (sanitized === '' || sanitized === '.') {
      onUpdateJob({ ...job, pay_rate: 0, cost_rate: 0 });
      return;
    }
    if (Number.isFinite(Number(sanitized))) {
      commitPay(sanitized);
    }
  };

  const rangeLabel = range
    ? `Market range: $${(range.minimum / 100).toFixed(2)} – $${(range.maximum / 100).toFixed(2)}`
    : null;

  return (
    <RateCard>
      <CardTopRow>
        <SkillTitle>Set Pay Rate for {skillName}</SkillTitle>
        <TotalHoursLabel>
          Total Hours: <strong>{hours.toFixed(2)}</strong>
        </TotalHoursLabel>
      </CardTopRow>

      <RateRow>
        <RateLabel>Pay Rate</RateLabel>
        <RateInputShell>
          <RateInputDollar>$</RateInputDollar>
          <RateInput
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={payInput}
            style={{
              color:
                tone === 'bad'
                  ? '#DC1515'
                  : tone === 'avg'
                    ? '#EC7F00'
                    : tone === 'good'
                      ? '#45A735'
                      : '#1C1C1C',
            }}
            onChange={(event) => handlePayInputChange(event.target.value)}
            onBlur={() => commitPay(payInput)}
          />
        </RateInputShell>
      </RateRow>

      <RatingRow tone={tone}>
        <RatingDot tone={tone} />
        <span>{messageForTone(tone)}</span>
      </RatingRow>

      {rangeLabel ? <MutedNote>{rangeLabel}</MutedNote> : null}
    </RateCard>
  );
};

const PayRateStep = ({
  jobs,
  onUpdateJobs,
  skills,
  billingAccount,
  onValidityChange,
}: PayRateStepProps) => {
  const [ranges, setRanges] = useState<Record<number, RateRange>>({});

  const updateJobAt = (index: number, next: JobDraftV2) => {
    const copy = [...jobs];
    copy[index] = next;
    onUpdateJobs(copy);
  };

  const handleRangeResolved = (index: number, range: RateRange) => {
    setRanges((prev) => {
      const current = prev[index];
      if (
        (current?.minimum ?? null) === (range?.minimum ?? null) &&
        (current?.maximum ?? null) === (range?.maximum ?? null)
      ) {
        return prev;
      }
      return { ...prev, [index]: range };
    });
  };

  // Continue is gated on every job's pay rate meeting that job's minimum.
  // Jobs whose range failed to load fall through as valid so a backend
  // hiccup never blocks order placement.
  useEffect(() => {
    if (!onValidityChange) return;
    const valid = jobs.every((job, index) => {
      const range = ranges[index];
      if (!range || range.minimum <= 0) return (job.pay_rate || 0) > 0;
      return (job.pay_rate || 0) >= range.minimum;
    });
    onValidityChange(valid);
  }, [jobs, ranges, onValidityChange]);

  if (!billingAccount) {
    return (
      <PageShell>
        <StepHeading>Set Pay Rate</StepHeading>
        <StepSubheading>
          Select a billing account to configure pay rates.
        </StepSubheading>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <StepHeading>Set Pay Rate</StepHeading>
      <StepSubheading>
        Choose a pay rate for each job. Bill rate is calculated automatically
        from the markup percent on this billing account.
      </StepSubheading>

      <JobList>
        {jobs.map((job, index) => (
          <JobRateCard
            key={index}
            job={job}
            index={index}
            skills={skills}
            billingAccountId={billingAccount.id}
            markupPercent={billingAccount.markup_percent ?? 0}
            onUpdateJob={(next) => updateJobAt(index, next)}
            onRangeResolved={handleRangeResolved}
          />
        ))}
      </JobList>
    </PageShell>
  );
};

export default PayRateStep;
